import React, { useEffect, useState } from "react";
import { Card, Popconfirm, Switch, Tag } from "antd";
import Heading from "../../../component/common/Heading";
import useHttp from "../../../hooks/use-http";
import CONSTANTS from "../../../util/constant/CONSTANTS";

// Global outage override for hospitality QR check-in.
// whatsappOutageMenuMode === true  -> outage mode ON  -> menu served everywhere
// whatsappOutageMenuMode === false -> normal mode      -> WhatsApp check-in everywhere
// This flips EVERY venue at once, so a confirm is required before toggling.
const HospitalityGlobalSwitch = () => {
  const api = useHttp();
  const [outageMenuMode, setOutageMenuMode] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchState = () => {
    const GET_API = { ...CONSTANTS.API.getHospitalityGlobal };
    api.sendRequest(GET_API, (res) => {
      // response body: { whatsappOutageMenuMode: boolean }
      const value = res?.data?.whatsappOutageMenuMode ?? res?.whatsappOutageMenuMode;
      setOutageMenuMode(!!value);
      setLoaded(true);
    });
  };

  useEffect(() => {
    fetchState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `next` is the value the switch would move to.
  const applyToggle = (next) => {
    const SET_API = { ...CONSTANTS.API.setHospitalityGlobal };
    api.sendRequest(
      SET_API,
      (res) => {
        const value = res?.data?.whatsappOutageMenuMode ?? res?.whatsappOutageMenuMode ?? next;
        setOutageMenuMode(!!value);
      },
      { whatsappOutageMenuMode: next },
      next
        ? "Outage mode ON — menu is now served everywhere"
        : "Outage mode OFF — WhatsApp check-in restored everywhere"
    );
  };

  const isOn = outageMenuMode === true; // outage (menu-everywhere) mode
  // The switch is "checked" when WhatsApp check-in is ON (normal),
  // i.e. the inverse of outage mode, so the label reads naturally.
  const whatsappCheckInOn = loaded ? !isOn : true;

  return (
    <Card className="my-5 select-text">
      <Heading>Hospitality — Global QR Check-in</Heading>
      <div className="mt-3">
        <p className="text-xl mt-1">
          WhatsApp check-in — turn OFF to serve the menu everywhere (outage mode)
        </p>
        <p className="text-slate-500 mt-1">
          This affects <b>ALL venues</b> at once. When OFF, every QR scan skips
          WhatsApp and goes straight to the menu.
        </p>

        <div className="flex items-center mt-3">
          <Popconfirm
            title={
              whatsappCheckInOn
                ? "Turn OFF WhatsApp check-in for ALL venues (enter outage mode)?"
                : "Turn ON WhatsApp check-in for ALL venues (exit outage mode)?"
            }
            okText="Yes, apply to all venues"
            cancelText="Cancel"
            disabled={!loaded || api.isLoading}
            onConfirm={() => applyToggle(whatsappCheckInOn /* toggling => new outage value */)}
          >
            <Switch
              checked={whatsappCheckInOn}
              loading={!loaded || api.isLoading}
              checkedChildren="WhatsApp ON"
              unCheckedChildren="Menu (outage)"
            />
          </Popconfirm>

          <span className="ml-4">
            {loaded ? (
              isOn ? (
                <Tag color="volcano">Outage mode — menu everywhere</Tag>
              ) : (
                <Tag color="green">Normal — WhatsApp check-in</Tag>
              )
            ) : (
              <Tag>Loading…</Tag>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default HospitalityGlobalSwitch;
